import React, { useState, useEffect } from 'react';
import { X, MapPin, Globe, Phone, Clock, Star, Filter, Search, Package, TrendingUp, ShoppingCart, Eye, Zap, ChartBar  } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { getProductImage } from '../static/ProductPlaceHolder';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../arrangement/ThemeContext';
// Category icons - you'll replace these with your saved SVG components
const CategoryIcons = {
  flower: () => <img src="/branding/menu/flower.svg" alt="Flower" style={{ width: '3rem', height: '3rem', filter: 'brightness(0) invert(1)' }} />,
  cart: () => <img src="/branding/menu/cart.svg" alt="Cartridge/Vape" style={{ width: '3rem', height: '3rem', filter: 'brightness(0) invert(1)' }} />,
  edible: () => <img src="/branding/menu/edibble.svg" alt="Edible" style={{ width: '3rem', height: '3rem', filter: 'brightness(0) invert(1)' }} />,
  'pre-roll': () => <img src="/branding/menu/prerol.svg" alt="Pre-roll" style={{ width: '3rem', height: '3rem', filter: 'brightness(0) invert(1)' }} />,
  concentrate: () => <img src="/branding/menu/_con.svg" alt="Concentrate" style={{ width: '3rem', height: '3rem', filter: 'brightness(0) invert(1)' }} />,
  other: () => <img src="/branding/menu/Frame.svg" alt="Concentrate" style={{ width: '3rem', height: '3rem', filter: 'brightness(0) invert(1)' }} />
};

export const DispensaryDetailModal = ({ isOpen, onClose, dispensaryId }) => {
  // Mobile detection
  const isMobile = window.innerWidth <= 768;
  const navigate = useNavigate();

  const { colors } = useTheme();
  
  const [dispensary, setDispensary] = useState(null);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total_pages: 1,
    current_page: 1,
    per_page: 50,
    total_products: 0,
    has_prev: false,
    has_next: false,
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [categoryFilter, inStockOnly, sortBy, searchTerm]);

  // Fetch data when page or filters change
  useEffect(() => {
    if (isOpen && dispensaryId) {
      fetchDispensaryDetails();
    }
  }, [isOpen, dispensaryId, categoryFilter, inStockOnly, sortBy, page]);

  const fetchDispensaryDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        ...(categoryFilter && { category: categoryFilter }),
        in_stock: inStockOnly.toString(),
        sort_by: sortBy,
        page: page.toString(),
        limit: '50'
      });

      const response = await fetch(`${API_URL}/api/dispensaries/${dispensaryId}/details?${queryParams}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dispensary details');
      }

      const data = await response.json();
      setDispensary(data.dispensary);
      setProducts(data.products);
      setStats(data.stats);
      
      if (data.pagination) {
        setPagination(data.pagination);
      }
      
    } catch (err) {
      setError(err.message);
      console.error('Error fetching dispensary details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering for search term
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = stats?.categories || [];
  const uniqueCategories = Object.keys(categories);

  const handlePreviousPage = () => {
    if (pagination.has_prev) {
      setPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setPage(prev => prev + 1);
    }
  };

  const handleCategorySelect = (category) => {
    setCategoryFilter(category === categoryFilter ? '' : category);
  };
  const handleProductClick = (product) => {
    console.log('product', product)
    navigate(`/products/${product.product_id}`);
  };

  const ProductCard = ({ product }) => (
    
    <div 
    onClick={() => handleProductClick(product)}
    style={{
      backgroundColor: '#2a2a2a',
      border: '1px solid #333',
      borderRadius: '12px',
      padding: isMobile ? '12px' : '16px',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      opacity: product.in_stock ? 1 : 0.7,
      position: 'relative'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = '#00d4aa';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '#333';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
    >
      {/* Product Image */}
      <div style={{
        width: '100%',
        height: isMobile ? '100px' : '120px',
        marginBottom: '12px',
        borderRadius: '8px',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #333 0%, #222 100%)',
        position: 'relative'
      }}>
        <img
          src={product?.cover_image?.imageUrl || getProductImage(product.type)}
          alt={product.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            const fallback = document.createElement('div');
            fallback.style.cssText = 'display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 12px;';
            fallback.innerHTML = '📦';
            e.target.parentElement.appendChild(fallback);
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
          }}
        />
        {product.image_count > 1 && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(0, 0, 0, 0.7)',
            color: '#fff',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: '600'
          }}>
            +{product.image_count - 1}
          </div>
        )}
      </div>

      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        background: 'rgba(0, 0, 0, 0.8)',
        color: '#00d4aa',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '9px',
        fontWeight: '600',
        zIndex: 10
      }}>
        {product.external_platform || 'Jane'}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ 
            margin: '0 0 4px 0', 
            fontSize: isMobile ? '14px' : '16px', 
            fontWeight: '600', 
            color: '#e5e5e5',
            lineHeight: '1.3'
          }}>
            {product.name}
          </h4>
          <p style={{ margin: 0, fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
            {product.brand} • {product.form}
          </p>
        </div>
        
        <div style={{
          background: product.in_stock 
            ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' 
            : 'linear-gradient(135deg, #666 0%, #555 100%)',
          color: product.in_stock ? '#0a0a0a' : '#ccc',
          padding: isMobile ? '3px 6px' : '4px 8px',
          borderRadius: '6px',
          fontSize: isMobile ? '10px' : '12px',
          fontWeight: '600'
        }}>
          {product.in_stock ? 'In Stock' : 'Out of Stock'}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{
          background: 'rgba(0, 212, 170, 0.2)',
          color: '#00d4aa',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: '600'
        }}>
          {product.type.toUpperCase()}
        </div>
        
        {product.thc && (
          <div style={{
            background: 'rgba(139, 92, 246, 0.2)',
            color: '#8b5cf6',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: '600'
          }}>
            {product.thc} THC
          </div>
        )}
        
        {product.cbd && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.2)',
            color: '#22c55e',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: '600'
          }}>
            {product.cbd} CBD
          </div>
        )}
        
        <div style={{
          background: 'rgba(245, 158, 11, 0.2)',
          color: '#f59e0b',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: '600'
        }}>
          {product.variant}
        </div>
      </div>

      {product.effects && product.effects.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '12px' }}>
          {product.effects.slice(0, isMobile ? 2 : 3).map((effect, index) => (
            <span key={effect} style={{
              fontSize: '9px',
              padding: '2px 4px',
              backgroundColor: index % 2 === 0 ? 'rgba(139, 92, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)',
              color: index % 2 === 0 ? '#8b5cf6' : '#f59e0b',
              borderRadius: '4px',
              fontWeight: '500'
            }}>
              {effect}
            </span>
          ))}
          {product.effects.length > (isMobile ? 2 : 3) && (
            <span style={{
              fontSize: '9px',
              padding: '2px 4px',
              backgroundColor: 'rgba(156, 163, 175, 0.15)',
              color: '#9ca3af',
              borderRadius: '4px',
              fontWeight: '500'
            }}>
              +{product.effects.length - (isMobile ? 2 : 3)} more
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', color: '#00d4aa' }}>
          {product.price}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {product.rating && product.rating > 0 ? (
            <>
              <Star size={12} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
              <span style={{ fontSize: '12px', color: '#e5e5e5', fontWeight: '500' }}>
                {product.rating}
              </span>
            </>
          ) : (
            <span style={{ fontSize: '12px', color: '#888', fontWeight: '500' }}>
              No reviews
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: isMobile ? '12px' : '20px',
        width: '100%',
        maxWidth: isMobile ? '100%' : '1400px',
        maxHeight: isMobile ? '100%' : '90vh',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? '16px' : '24px',
          borderBottom: `1px solid ${colors.border}`,
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <h2 style={{
                margin: 0,
                fontSize: isMobile ? '20px' : '28px',
                fontWeight: '700',
                color: '#e5e5e5',
                marginBottom: '8px'
              }}>
                {dispensary?.name || 'Loading...'}
              </h2>
              
              {dispensary && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888' }}>
                    <MapPin size={14} />
                    <span style={{ fontSize: isMobile ? '12px' : '14px' }}>
                      {dispensary.address && `${dispensary.address}, `}
                      {dispensary.city}, {dispensary.state} {dispensary.postal_code}
                    </span>
                  </div>
                  
                  {dispensary.website && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888' }}>
                      <Globe size={14} />
                      <a 
                        href={dispensary.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ fontSize: isMobile ? '12px' : '14px', color: '#00d4aa', textDecoration: 'none' }}
                      >
                        Visit Website
                      </a>
                    </div>
                  )}
                </div>
              )}

              {stats && (
                <div style={{ display: 'flex', gap: isMobile ? '8px' : '16px', flexWrap: 'wrap' }}>
                  <div style={{
                    background: 'rgba(0, 212, 170, 0.1)',
                    padding: isMobile ? '6px 8px' : '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 212, 170, 0.3)'
                  }}>
                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#00d4aa' }}>
                      {stats.total_products}
                    </div>
                    <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#888' }}>Products</div>
                  </div>
                  
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    padding: isMobile ? '6px 8px' : '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(139, 92, 246, 0.3)'
                  }}>
                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#8b5cf6' }}>
                      {stats.in_stock_products}
                    </div>
                    <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#888' }}>In Stock</div>
                  </div>
                  
                  <div style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    padding: isMobile ? '6px 8px' : '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(245, 158, 11, 0.3)'
                  }}>
                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#f59e0b' }}>
                      {stats.average_rating}
                    </div>
                    <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#888' }}>Avg Rating</div>
                  </div>
                  
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    padding: isMobile ? '6px 8px' : '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(34, 197, 94, 0.3)'
                  }}>
                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#22c55e' }}>
                      {uniqueCategories.length}
                    </div>
                    <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#888' }}>Categories</div>
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#888';
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filters and Search - Mobile responsive layout */}
        <div style={{
          padding: isMobile ? '12px 16px' : '20px 24px',
          borderBottom: `1px solid ${colors.border}`,
          background: colors.bgCard,
          flexShrink: 0
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '1fr auto auto auto', 
            gap: '12px', 
            alignItems: 'center' 
          }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888'
              }} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: isMobile ? '85%' : '100%', 
                  paddingLeft: '40px',
                  paddingRight: '12px',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Mobile: Stack filters */}
            <div style={{
              display: isMobile ? 'grid' : 'contents',
              gridTemplateColumns: isMobile ? '1fr 1fr' : undefined,
              gap: isMobile ? '12px' : undefined
            }}>
              <div style={{ position: 'relative' }}>
                <Filter size={16} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#888'
                }} />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  style={{
                    width: '100%',
                    paddingLeft: '40px',
                    paddingRight: '12px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    appearance: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Categories</option>
                  {uniqueCategories.map((category) => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ position: 'relative' }}>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    width: '100%',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    appearance: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="name">Sort by Name</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                  <option value="rating">Rating</option>
                </select>
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                gridColumn: isMobile ? '1 / -1' : undefined,
                justifyContent: isMobile ? 'center' : undefined
              }}>
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) => setInStockOnly(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ color: '#e5e5e5', fontSize: '14px' }}>In Stock Only</span>
              </div>
            </div>
          </div>
        </div>

        {/* Category Ribbon Menu */}
        <div style={{
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderBottom: `1px solid ${colors.border}`,
          background: colors.bgCard,
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            gap: isMobile ? '8px' : '12px',
            alignItems: 'center',
            overflowX: 'auto',
            paddingBottom: '4px'
          }}>
            {/* All Categories Option */}
            <div
              onClick={() => handleCategorySelect('')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: isMobile ? '8px 12px' : '12px 16px',
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: categoryFilter === '' ? 'rgba(0, 212, 170, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: categoryFilter === '' ? '2px solid #00d4aa' : '2px solid transparent',
                transition: 'all 0.2s ease',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (categoryFilter !== '') {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (categoryFilter !== '') {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }
              }}
            >
              <Package size={isMobile ? 20 : 24} style={{ 
                color: categoryFilter === '' ? '#00d4aa' : '#888',
                marginBottom: '6px'
              }} />
              <span style={{
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: '600',
                color: categoryFilter === '' ? '#00d4aa' : '#e5e5e5'
              }}>
                All
              </span>
            </div>

            {/* Category Options */}
            {['flower', 'cart', 'edible', 'pre-roll', 'concentrate', 'other'].map((category) => {
              const categoryCount = categories[category] || 0;
              const IconComponent = CategoryIcons[category];
              
              return (
                <div
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: isMobile ? '' : '12px 16px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: categoryFilter === category ? 'rgba(0, 212, 170, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                    border: categoryFilter === category ? '2px solid #00d4aa' : '2px solid transparent',
                    transition: 'all 0.2s ease',
                    minWidth: 'fit-content',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (categoryFilter !== category) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (categoryFilter !== category) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                >
                  <div style={{
                    marginBottom: '6px',
                    color: categoryFilter === category ? '#00d4aa' : '#888',
                    transform: isMobile ? 'scale(0.8)' : 'scale(1)'
                  }}>
                    <IconComponent />
                  </div>
                  <span style={{
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '600',
                    color: categoryFilter === category ? '#00d4aa' : '#e5e5e5',
                    marginBottom: '2px'
                  }}>
                    {category.charAt(0).toUpperCase() + category.slice(1).replace('/', '/')}
                  </span>
                  {categoryCount > 0 && (
                    <span style={{
                      fontSize: isMobile ? '9px' : '10px',
                      color: '#888',
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '1px 6px',
                      borderRadius: '8px'
                    }}>
                      {categoryCount}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
          <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0
        }}>
          {loading && (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              color: '#e5e5e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1
            }}>
              <div>Loading...</div>
            </div>
          )}
          
          {error && (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1
            }}>
              Error: {error}
            </div>
          )}

          {!loading && !error && (
            <div style={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                flex: 1,
                padding: isMobile ? '16px' : '24px',
                backgroundColor: '#1a1a1a',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                {filteredProducts.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    color: '#888', 
                    padding: '24px' 
                  }}>
                    No products found matching your criteria.
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile 
                      ? 'repeat(auto-fill, minmax(150px, 1fr))' 
                      : 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: isMobile ? '12px' : '16px'
                  }}>
                    {filteredProducts.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                )}
              </div>

              {pagination.total_pages > 1 && (
                <div style={{
                  padding: isMobile ? '12px 16px' : '16px 24px',
                  borderTop: '1px solid #333',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#2a2a2a',
                  flexShrink: 0,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '12px' : '0'
                }}>
                  <div style={{ 
                    color: '#e5e5e5', 
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: isMobile ? 'center' : 'left'
                  }}>
                    Page {pagination.current_page} of {pagination.total_pages} 
                    {!isMobile && ` (${pagination.total_products} total products)`}
                    {isMobile && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                        {pagination.total_products} total products
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={handlePreviousPage}
                      disabled={!pagination.has_prev}
                      style={{
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        backgroundColor: pagination.has_prev ? '#00d4aa' : '#333',
                        color: pagination.has_prev ? '#0a0a0a' : '#888',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: pagination.has_prev ? 'pointer' : 'not-allowed',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '600'
                      }}
                    >
                      {isMobile ? 'Prev' : 'Previous'}
                    </button>
                    <span style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: isMobile ? '0 8px' : '0 12px', 
                      color: '#e5e5e5',
                      fontSize: isMobile ? '12px' : '14px'
                    }}>
                      {pagination.current_page}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={!pagination.has_next}
                      style={{
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        backgroundColor: pagination.has_next ? '#00d4aa' : '#333',
                        color: pagination.has_next ? '#0a0a0a' : '#888',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: pagination.has_next ? 'pointer' : 'not-allowed',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '600'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};